import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { FileSearch, Loader2, Upload, FileType, Sparkles, CheckCircle } from 'lucide-react';
import { sevBadge, CostBadge, ScoreRing, handleApiError } from './shared';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DesignReview = ({ locked, cost, onComplete }) => {
  const navigate = useNavigate();
  const [reviewType, setReviewType] = useState('hld');
  const [systemName, setSystemName] = useState('');
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState(null);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const review = async () => {
    if (!systemName || !file) { toast.error('System name and a design document are required'); return; }
    setRunning(true);
    const fd = new FormData();
    fd.append('review_type', reviewType); fd.append('system_name', systemName); fd.append('notes', notes); fd.append('file', file);
    try {
      const r = await axios.post(`${API}/org-security/design-review`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(r.data); onComplete?.(); toast.success(`Review complete: ${r.data.findings.length} findings`);
    } catch (err) { handleApiError(err, toast, navigate); } finally { setRunning(false); }
  };

  return (
    <div className="space-y-6" data-testid="design-review-module">
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base font-heading flex items-center gap-2"><FileSearch className="h-4 w-4 text-primary" /> Architecture document review</CardTitle></CardHeader>
        <CardContent className="space-y-5">
          <div className="grid grid-cols-2 gap-3 max-w-md">
            {[{ v: 'hld', l: 'HLD', d: 'High-Level Design: zones, trust boundaries, data flows' }, { v: 'lld', l: 'LLD', d: 'Low-Level Design: components, interfaces, configs' }].map(o => (
              <button key={o.v} onClick={() => setReviewType(o.v)} className={`p-3.5 rounded-xl border text-left transition-all ${reviewType === o.v ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/30'}`} data-testid={`review-type-${o.v}`}>
                <p className="text-sm font-semibold">{o.l}</p><p className="text-[11px] text-muted-foreground mt-0.5">{o.d}</p>
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><Label className="mb-2 block">System / platform name</Label><Input value={systemName} onChange={e => setSystemName(e.target.value)} placeholder="Payments Platform v2" data-testid="design-system-input" /></div>
            <div><Label className="mb-2 block">Reviewer notes (optional)</Label><Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Focus on PCI scope, external integrations..." data-testid="design-notes-input" /></div>
          </div>
          <div className="p-4 rounded-xl border border-dashed hover:border-primary/40 transition-colors">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-primary/10"><Upload className="h-5 w-5 text-primary" /></div>
              <div className="flex-1"><p className="text-sm font-medium">Upload {reviewType.toUpperCase()} document</p><p className="text-xs text-muted-foreground">PDF, DOCX, TXT or Markdown · max 15MB</p></div>
              <label className="cursor-pointer"><input type="file" accept=".pdf,.docx,.txt,.md" className="hidden" onChange={e => setFile(e.target.files?.[0] || null)} data-testid="design-file-input" /><Button variant="outline" size="sm" asChild><span><Upload className="h-3.5 w-3.5 mr-1" /> Browse</span></Button></label>
            </div>
            {file && <div className="mt-3 flex items-center gap-2 p-2 rounded-lg bg-muted/50"><FileType className="h-4 w-4 text-primary" /><span className="text-sm font-mono truncate flex-1" data-testid="design-file-name">{file.name}</span><Button variant="ghost" size="sm" className="h-6 px-2" onClick={() => setFile(null)}>Remove</Button></div>}
          </div>
          <Button onClick={review} disabled={locked || running} className="gap-2" data-testid="design-review-btn">{running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {running ? 'AI is reviewing the design...' : 'Run AI security review'} <CostBadge cost={cost} /></Button>
        </CardContent>
      </Card>

      {result && (
        <Card className="border-primary/30" data-testid="design-result">
          <CardHeader className="pb-3"><CardTitle className="text-base font-heading flex items-center gap-2">{result.review_type.toUpperCase()} review — {result.name} <Badge variant="outline" className="text-[10px]">{result.method === 'ai' ? 'AI-powered' : 'Heuristic'}</Badge></CardTitle></CardHeader>
          <CardContent className="space-y-5">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-center">
              <ScoreRing score={result.score} label="Security score" />
              {['critical', 'high', 'medium', 'low'].map(s => <div key={s} className={`text-center p-3 rounded-lg ${sevBadge[s]}`}><p className="text-xl font-bold">{result.severity_counts[s]}</p><p className="text-[10px] capitalize">{s}</p></div>)}
            </div>
            {result.summary && <p className="text-sm text-muted-foreground p-3 rounded-lg bg-muted/40" data-testid="design-summary">{result.summary}</p>}
            {result.strengths?.length > 0 && <div className="flex flex-wrap gap-2">{result.strengths.map((s, i) => <span key={i} className="text-xs px-2.5 py-1 rounded-full border border-green-500/30 text-green-500 bg-green-500/10 flex items-center gap-1"><CheckCircle className="h-3 w-3" /> {s}</span>)}</div>}
            <div className="space-y-2">
              <p className="text-sm font-medium">Findings ({result.findings.length})</p>
              {result.findings.map((f, i) => (
                <div key={i} className="p-3 rounded-lg border bg-card" data-testid={`design-finding-${i}`}>
                  <div className="flex items-center gap-2 mb-1"><Badge variant="outline" className={`text-[9px] uppercase ${sevBadge[f.severity] || ''}`}>{f.severity}</Badge><p className="text-sm font-medium">{f.title}</p><span className="text-[11px] text-muted-foreground ml-auto">{f.category}</span></div>
                  <p className="text-xs text-muted-foreground">{f.description}</p>
                  {f.recommendation && <p className="text-xs mt-1.5"><span className="font-semibold">Recommendation:</span> {f.recommendation}</p>}
                  {f.control_reference && <p className="text-[11px] text-muted-foreground mt-1 font-mono">{f.control_reference}</p>}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
